import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, LayoutGrid, Layers, Grid3x3, RotateCcw, Loader2, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ComparisonShareDialog from '../share/ComparisonShareDialog';
import usePairAlignment from '../../hooks/usePairAlignment';
import SliderPanel from './comparison/SliderPanel';
import SideBySidePair from './comparison/SideBySidePair';
import OverlayPanel from './comparison/OverlayPanel';
import { SINGLE_W, PAIR_W, QUAD_W } from './comparison/PanelBits';

const STORAGE_MODE = 'trs_cmp_mode';
const STORAGE_GRID = 'trs_cmp_grid';

function readStored(key, fallback) {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}

// usePairAlignment returns alignState.label as hardcoded English (it is not
// aware of i18n). Map those exact strings to translation keys here, at the
// one place the viewer renders the status chip, without touching the hook.
const ALIGN_LABEL_KEYS = {
  'Manual only': ['photoCompare.alignManual', 'Manual only'],
  'Partial': ['photoCompare.alignPartial', 'Partial'],
  'Auto-aligned': ['photoCompare.alignAuto', 'Auto-aligned'],
};

export default function PhotoComparisonViewer({
  pairs,
  onKeypointsComputed,
  handle = '',
  coachMode = false,
}) {
  const [mode, setMode] = useState(() => readStored(STORAGE_MODE, 'slider'));
  const [gridOn, setGridOn] = useState(() => readStored(STORAGE_GRID, 'off') === 'on');
  const [sliderPos, setSliderPos] = useState(50);
  const [opacity, setOpacity] = useState(0.5);
  const [blend, setBlend] = useState('normal');
  const [shareOpen, setShareOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => { try { localStorage.setItem(STORAGE_MODE, mode); } catch {} }, [mode]);
  useEffect(() => { try { localStorage.setItem(STORAGE_GRID, gridOn ? 'on' : 'off'); } catch {} }, [gridOn]);

  // Exactly two hook calls, always, in the same order. Calling usePairAlignment
  // inside pairs.map() would be a conditional hook — rules-of-hooks is an ERROR
  // in craco.config.js and would fail the Vercel build, not just lint.
  const pairA = usePairAlignment({ pair: pairs[0] || EMPTY_PAIR, onKeypointsComputed });
  const pairB = usePairAlignment({ pair: pairs[1] || EMPTY_PAIR, onKeypointsComputed });
  const active = pairs.length > 1 ? [pairA, pairB] : [pairA];

  // Every string the panels render or announce comes from here. The panels
  // hold no literals of their own, so a new panel cannot reintroduce
  // hardcoded English into a translated surface.
  const labels = {
    before: t('photoCompare.before', 'BEFORE'),
    after: t('photoCompare.after', 'AFTER'),
    missing: t('photoCompare.noPhotoForPose', 'No photo for this pose'),
    mismatch: t('photoCompare.autoMatchPoseMismatch', 'Poses too different to auto-match'),
    wipePosition: t('photoCompare.wipePosition', 'Wipe position'),
  };

  const computing = active.some(p => p.computing);
  const alignState = worstAlignState(active);
  const alignKey = ALIGN_LABEL_KEYS[alignState.label];
  const alignLabel = alignKey ? t(alignKey[0], alignKey[1]) : alignState.label;
  const canShare = active.every(p => p.before.url && p.after.url);
  const resetAll = () => active.forEach(p => p.reset());

  // Slider and Overlay put ONE panel per pair; Side-by-side puts two. So a
  // 2-pair Side-by-side is the only 2x2 case, and it is the only one that has
  // to halve its column width to stay inside the 70vh height budget.
  const canvasWidth =
    mode === 'sbs' && active.length > 1 ? QUAD_W
    : mode === 'sbs' || active.length > 1 ? PAIR_W
    : SINGLE_W;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap mx-auto" style={{ width: canvasWidth }}>
        <div className="inline-flex rounded-lg bg-muted p-0.5">
          {[
            { key: 'slider', icon: ArrowLeftRight, label: t('photoCompare.modeSlider', 'Slider') },
            { key: 'sbs', icon: LayoutGrid, label: t('photoCompare.modeSideBySide', 'Side-by-side') },
            { key: 'overlay', icon: Layers, label: t('photoCompare.modeOverlay', 'Overlay') },
          ].map(({ key, icon: Icon, label }) => (
            <button key={key} onClick={() => setMode(key)}
              className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                mode === key ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}>
              <Icon className="h-3 w-3" /> {label}
            </button>
          ))}
        </div>

        <button onClick={() => setGridOn(g => !g)} aria-label={t('photoCompare.toggleGrid', 'Toggle grid')} title={t('photoCompare.toggleGrid', 'Toggle grid')}
          className={`p-1.5 rounded-md text-xs ${gridOn ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
          <Grid3x3 className="h-3.5 w-3.5" />
        </button>

        <button onClick={resetAll} aria-label={t('photoCompare.resetFineTune', 'Reset fine-tune')} title={t('photoCompare.resetFineTune', 'Reset fine-tune')}
          className="p-1.5 rounded-md text-xs bg-muted text-muted-foreground hover:bg-muted/80">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>

        <button onClick={() => setShareOpen(true)} disabled={!canShare}
          className="p-1.5 rounded-md text-xs bg-[#FF5C00]/15 text-[#FF5C00] hover:bg-[#FF5C00]/25
                     disabled:opacity-40 disabled:pointer-events-none"
          title={t('comparisonShare.openLabel')} aria-label={t('comparisonShare.openLabel')}>
          <Share2 className="h-3.5 w-3.5" />
        </button>

        <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${
          alignState.tone === 'green' ? 'bg-emerald-500/15 text-emerald-600' :
          alignState.tone === 'amber' ? 'bg-amber-500/15 text-amber-600' :
          'bg-zinc-500/15 text-zinc-500'
        }`}>
          {computing ? <><Loader2 className="inline h-3 w-3 animate-spin" /> {t('photoCompare.preparing', 'Preparing…')}</> : alignLabel}
        </span>
      </div>

      <div className="mx-auto" style={{ width: canvasWidth }}>
        {mode === 'slider' && (
          <div className={active.length > 1 ? 'grid grid-cols-2 gap-1' : ''}>
            {active.map((p, i) => (
              <SliderPanel key={pairs[i].id} pair={p} sliderPos={sliderPos} onSliderPos={setSliderPos}
                           gridOn={gridOn} labels={labels} testIdSuffix={i === 0 ? '' : `-${i + 1}`}
                           computing={p.computing} />
            ))}
          </div>
        )}

        {mode === 'sbs' && (
          <div className="grid grid-cols-2 gap-1">
            {active.map((p, i) => (
              <SideBySidePair key={pairs[i].id} pair={p} gridOn={gridOn} labels={labels}
                              testIdSuffix={i === 0 ? '' : `-${i + 1}`} />
            ))}
          </div>
        )}

        {mode === 'sbs' && computing && (
          <div className="mt-1 text-center text-xs text-muted-foreground">
            <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> {t('photoCompare.preparing', 'Preparing…')}
          </div>
        )}

        {mode === 'overlay' && (
          <div className="space-y-2">
            <div className={active.length > 1 ? 'grid grid-cols-2 gap-1' : ''}>
              {active.map((p, i) => (
                <OverlayPanel key={pairs[i].id} pair={p} opacity={opacity} blend={blend}
                              gridOn={gridOn} labels={labels} testIdSuffix={i === 0 ? '' : `-${i + 1}`}
                              computing={p.computing} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{labels.before}</span>
              <input type="range" min="0" max="100" value={opacity * 100}
                     aria-label={t('photoCompare.overlayOpacity', 'Overlay opacity')}
                     onChange={e => setOpacity(Number(e.target.value) / 100)}
                     className="flex-1 accent-primary" />
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{labels.after}</span>
              <button onClick={() => setBlend(b => (b === 'difference' ? 'normal' : 'difference'))}
                className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${
                  blend === 'difference' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                {t('photoCompare.diff', 'Diff')}
              </button>
            </div>
          </div>
        )}
      </div>

      <ComparisonShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        coachMode={coachMode}
        defaultTemplate={defaultTemplateFor(mode, active.length)}
        viewerProps={{ pairs: active, sliderPos, handle }}
      />
    </div>
  );
}

// A pair the hook can safely run on when only one pair is supplied. Its two
// null urls make every derived value null and every effect a no-op.
const EMPTY_PAIR = {
  id: '__empty__',
  poseType: { before: '', after: '' },
  before: { photoUrl: null, keypoints: null, checkInId: null, weightKg: null, bodyFat: null, date: null },
  after: { photoUrl: null, keypoints: null, checkInId: null, weightKg: null, bodyFat: null, date: null },
};

const TONE_RANK = { grey: 0, amber: 1, green: 2 };

function worstAlignState(pairs) {
  return pairs.reduce(
    (worst, p) => (TONE_RANK[p.alignState.tone] < TONE_RANK[worst.tone] ? p.alignState : worst),
    pairs[0].alignState,
  );
}

export function defaultTemplateFor(mode, pairCount) {
  if (pairCount > 1) return mode === 'slider' ? 'twinwipe' : 'quadgrid';
  return mode === 'slider' ? 'wipe' : 'split';
}
